import { create } from 'zustand';
import { useSpellCatalogStore } from './spellCatalogueSlice';
import {
  EMPTY_PLAYER_SLOT,
  PERSO_SLOT_COUNT,
  PlayerSlotContent,
  TrinketId,
  TrinketLevel,
} from '../types/trinket.types';
import { SketchModelId, SketchSlotName } from '../types/sketch.types';

const STORAGE_KEY = 'usagi_loadout';

const STARTING_TRINKET_LEVEL = 1;

/**
 * Trinkets, sketches and spells are unique-acquisition objects: ownership is keyed
 * by modelId — presence in the record/array IS the unlocked flag, the value is
 * the current trinket level. No instances, so a model can never be owned twice
 * (admin give + in-game unlock = single entry).
 */
interface LoadoutState {
  /** Owned major trinkets: modelId → current level. */
  majorTrinkets: Record<TrinketId, TrinketLevel>;
  /** Owned minor trinkets: modelId → current level. */
  minorTrinkets: Record<TrinketId, TrinketLevel>;
  /** Owned sketches (no level — fixed definitions). */
  sketches: SketchModelId[];
  equipped: {
    /** Equipped major trinket. `null` = neutral fist. */
    major: TrinketId | null;
    /** 4 character slots, indexed 0..3 (map to slot1..slot4). */
    slots: PlayerSlotContent[];
    sketches: Record<SketchSlotName, SketchModelId | null>;
  };
}

interface LoadoutActions {
  /** Returns true when newly unlocked — false (no-op) if already owned. */
  unlockMajor: (modelId: TrinketId, level?: TrinketLevel) => boolean;
  unlockMinor: (modelId: TrinketId, level?: TrinketLevel) => boolean;
  unlockSketch: (modelId: SketchModelId) => boolean;
  /** Sets the level of an owned trinket (major or minor). No-op if not owned. */
  setTrinketLevel: (modelId: TrinketId, level: TrinketLevel) => void;
  getMajorLevel: (modelId: TrinketId | null) => TrinketLevel | null;
  getMinorLevel: (modelId: TrinketId | null) => TrinketLevel | null;
  hasSketch: (modelId: SketchModelId | null) => boolean;

  // Equip mutations — the caller validates (anti-duplicate).
  equipMajor: (modelId: TrinketId | null) => void;
  setSlot: (slotIndex: number, content: PlayerSlotContent) => void;
  clearSlot: (slotIndex: number) => void;
  equipSketch: (slot: SketchSlotName, modelId: SketchModelId | null) => void;
}

function emptySlots(): PlayerSlotContent[] {
  return Array.from({ length: PERSO_SLOT_COUNT }, () => ({
    ...EMPTY_PLAYER_SLOT,
  }));
}

const DEFAULT_STATE: LoadoutState = {
  majorTrinkets: {},
  minorTrinkets: {},
  sketches: [],
  equipped: {
    major: null,
    slots: emptySlots(),
    sketches: { sketch1: null, sketch2: null },
  },
};

function load(): LoadoutState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<LoadoutState> & {
        majorTrinketInstances?: unknown;
      };
      // Pre-ownership-rework shape (instance arrays): no migration — reset.
      if (parsed.majorTrinketInstances !== undefined || !parsed.majorTrinkets) {
        return DEFAULT_STATE;
      }
      // Defensive: if the save has a different slot count, normalize to
      // PERSO_SLOT_COUNT rather than crash.
      if (parsed.equipped?.slots?.length !== PERSO_SLOT_COUNT) {
        parsed.equipped = {
          ...DEFAULT_STATE.equipped,
          ...parsed.equipped,
          slots: emptySlots(),
        };
      }
      return parsed as LoadoutState;
    }
  } catch {
    /* ignore */
  }
  return DEFAULT_STATE;
}

function persist(state: LoadoutState): void {
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      majorTrinkets: state.majorTrinkets,
      minorTrinkets: state.minorTrinkets,
      sketches: state.sketches,
      equipped: state.equipped,
    }),
  );
}

export function seedStarter(): void {
  const spellStore = useSpellCatalogStore.getState();
  if (spellStore.isUnlocked('outburst')) {
    return;
  }
  spellStore.unlock('outburst');
  spellStore.unlock('startle');
}

export const useLoadoutStore = create<LoadoutState & LoadoutActions>(
  (set, get) => ({
    ...load(),

    unlockMajor: (modelId, level = STARTING_TRINKET_LEVEL) => {
      if (get().majorTrinkets[modelId] !== undefined) {
        return false;
      }
      set((s) => {
        const next = {
          ...s,
          majorTrinkets: { ...s.majorTrinkets, [modelId]: level },
        };
        persist(next);
        return next;
      });
      return true;
    },

    unlockMinor: (modelId, level = STARTING_TRINKET_LEVEL) => {
      if (get().minorTrinkets[modelId] !== undefined) {
        return false;
      }
      set((s) => {
        const next = {
          ...s,
          minorTrinkets: { ...s.minorTrinkets, [modelId]: level },
        };
        persist(next);
        return next;
      });
      return true;
    },

    unlockSketch: (modelId) => {
      if (get().sketches.includes(modelId)) {
        return false;
      }
      set((s) => {
        const next = { ...s, sketches: [...s.sketches, modelId] };
        persist(next);
        return next;
      });
      return true;
    },

    setTrinketLevel: (modelId, level) => {
      set((s) => {
        if (s.majorTrinkets[modelId] !== undefined) {
          const next = {
            ...s,
            majorTrinkets: { ...s.majorTrinkets, [modelId]: level },
          };
          persist(next);
          return next;
        }
        if (s.minorTrinkets[modelId] !== undefined) {
          const next = {
            ...s,
            minorTrinkets: { ...s.minorTrinkets, [modelId]: level },
          };
          persist(next);
          return next;
        }
        return s;
      });
    },

    getMajorLevel: (modelId) =>
      modelId === null ? null : (get().majorTrinkets[modelId] ?? null),

    getMinorLevel: (modelId) =>
      modelId === null ? null : (get().minorTrinkets[modelId] ?? null),

    hasSketch: (modelId) =>
      modelId !== null && get().sketches.includes(modelId),

    equipMajor: (modelId) => {
      set((s) => {
        const next = {
          ...s,
          equipped: { ...s.equipped, major: modelId },
        };
        persist(next);
        return next;
      });
    },

    setSlot: (slotIndex, content) => {
      if (slotIndex < 0 || slotIndex >= PERSO_SLOT_COUNT) {
        return;
      }
      set((s) => {
        const slots = [...s.equipped.slots];
        slots[slotIndex] = content;
        const next = { ...s, equipped: { ...s.equipped, slots } };
        persist(next);
        return next;
      });
    },

    clearSlot: (slotIndex) => {
      if (slotIndex < 0 || slotIndex >= PERSO_SLOT_COUNT) {
        return;
      }
      set((s) => {
        const slots = [...s.equipped.slots];
        slots[slotIndex] = { ...EMPTY_PLAYER_SLOT };
        const next = { ...s, equipped: { ...s.equipped, slots } };
        persist(next);
        return next;
      });
    },

    equipSketch: (slot, modelId) => {
      set((s) => {
        const next = {
          ...s,
          equipped: {
            ...s.equipped,
            sketches: { ...s.equipped.sketches, [slot]: modelId },
          },
        };
        persist(next);
        return next;
      });
    },
  }),
);
